/**
 * The one place the precedence chain is resolved for the whole application.
 *
 * WHY THIS DOES NOT READ AuthContext, which was the first thing to reach for and is wrong twice
 * over. First, ORDER: TenantProvider sits above AuthProvider in main.jsx and Login renders inside
 * both, so a language provider that depended on auth could not also serve the tenant default without
 * a provider sandwich that any future edit could silently break. Second, and worse, TIMING:
 * AuthProvider's `initializeAuth` is ASYNC — on a page load it mints an access token from the refresh
 * cookie before `user` is populated — so a language derived from it would paint English first and
 * then swap. A flash of English is exactly the failure this feature exists to remove, on a screen
 * being read by somebody who cannot read English.
 *
 * So the account preference is read from the SAME localStorage row AuthProvider reads its stored user
 * from, synchronously, before first paint. `login()` writes that row before it redirects, and login
 * IS a full page navigation (routes/Login.jsx sets window.location.href), so first paint after a
 * sign-in already has the right answer.
 *
 * ⚠ SIGN-OUT IS NOT, and an earlier draft of this header said it was. The logout button people
 * actually press clears storage and calls `navigate('/login')` — an SPA route change — and this
 * provider sits above `<App/>` and never unmounts, so a mount-time read alone left the sign-in page
 * rendering in the departed user's language with the toggle unable to change it. Hence the
 * subscription below. See ./accountLanguage.js.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { useTenantBranding } from '../../contexts/TenantContext.jsx'
import { LANGUAGES, namespaceDict } from './dict'
import { makeT } from './t'
import { cacheAccountLanguage, readAccountLanguage, subscribeAccountLanguage } from './accountLanguage'
import { setAppDocumentLanguage } from './documentLanguage'
import {
  BASE_LANGUAGE, readStoredLanguage, rememberLanguage, resolveLanguage,
} from './languages'

const LanguageContext = createContext(null)

export function LanguageProvider({ children, offered = LANGUAGES }) {
  const branding = useTenantBranding()
  const tenantDefault = branding?.defaultLanguage || null

  // Read at mount and then owned by this component. `chosen` is rung (b): undefined means
  // "nothing explicit yet", which is different from '' — see resolveLanguage.
  const [account, setAccount] = useState(readAccountLanguage)
  const [chosen, setChosen] = useState(() => readStoredLanguage() || '')

  /**
   * Re-read the two stored rungs when somebody else changes them under us.
   *
   * There are exactly two things that do: SIGNING OUT, which is an SPA route change and therefore
   * never remounts this provider (shared/auth/signOut.js), and the boot-time reconciliation of the
   * account preference against the server (hooks/useAuth.jsx), which is how an admin setting
   * somebody's language FOR them reaches a session that is already open.
   *
   * BOTH rungs are re-read, not just the account, because a sign-out is a statement about the whole
   * chain: the person is gone, and whatever this browser was told to remember is now what decides.
   */
  useEffect(() => subscribeAccountLanguage(() => {
    setAccount(readAccountLanguage())
    setChosen(readStoredLanguage() || '')
  }), [])

  const lang = useMemo(() => resolveLanguage({
    userPreference: account,
    storedChoice: chosen,
    tenantDefault,
    offered,
  }), [account, chosen, tenantDefault, offered])

  // The document's own language attribute. Screen readers pronounce Spanish text with English
  // phonemes without it.
  //
  // ⚠ THIS ANSWER CAN BE OUTRANKED, and a bare assignment here was wrong on the one page that matters
  // most. The QR sticker sheet resolves its own language from a different set for a different person
  // (`pickLanguage`, over the languages that VERSION is written in, defaulted by the forms module's
  // `public_default_language` — not the company language this chain reads). So a hand could tap
  // Español on a sheet, read Spanish, and have this attribute still say `en` for the whole visit,
  // because nothing on that page ever writes the application's own language key. See
  // ./documentLanguage.js for why precedence rather than last-writer-wins.
  useEffect(() => { setAppDocumentLanguage(lang) }, [lang])

  /**
   * Choose a language.
   *
   * `persist` IS THE WHOLE DISTINCTION: a control that can save to the account writes RUNG (a), and
   * a control that cannot writes RUNG (b), this device. Never both.
   *
   * ⚠ IT USED TO WRITE BOTH, and that quietly pinned shared hardware. Two men share an office tablet.
   * The first opens his profile and saves Spanish — a statement about HIM — and the device pin it
   * also wrote outlived him, so the next man's sign-in page opened in a language he may not read,
   * with nothing on screen saying why. Worse, the only control that un-pins a device (`clearLanguage`)
   * also discards the account preference, so he could not undo the one without losing the other.
   *
   * The account (a) outranks the device (b), so writing only the account still changes the screen
   * immediately. And writing the account cache WITHOUT a server call would leave the browser and the
   * database disagreeing until the next login quietly overwrote the local copy — which is why the
   * cache write and the save travel together.
   *
   * The local write happens first and unconditionally: the language changes on screen immediately,
   * and a failed save means the choice is local for this session — strictly better than refusing to
   * change language because a network call did not land.
   */
  const setLanguage = useCallback((tag, { persist } = {}) => {
    const next = tag || ''
    if (typeof persist === 'function') {
      cacheAccountLanguage(next)
      setAccount(next || null)
      Promise.resolve(persist(next || null)).catch(() => {})
      return
    }
    rememberLanguage(next)
    setChosen(next)
  }, [])

  /**
   * Hand the decision back to the device and the company — NOT the same as choosing English.
   *
   * Clears the account preference AND records "no choice" on this device. Both are needed: a device
   * pin left in place (set on the sign-in page, which is the only control that writes one) would
   * simply move the pin from one rung to the next, and the person asking to follow their phone would
   * still not be following their phone. This is therefore also the way OUT of a device pin, which is
   * the reverse of the trap described on setLanguage above.
   */
  const clearLanguage = useCallback(({ persist } = {}) => {
    rememberLanguage('')
    setChosen('')
    if (typeof persist === 'function') {
      cacheAccountLanguage(null)
      setAccount(null)
      Promise.resolve(persist(null)).catch(() => {})
    }
  }, [])

  const value = useMemo(() => ({
    lang,
    offered,
    baseLanguage: BASE_LANGUAGE,
    /** What the ACCOUNT says, or null for "never chosen" — what a settings screen has to show. */
    accountLanguage: account,
    tenantDefault,
    setLanguage,
    clearLanguage,
  }), [lang, offered, account, tenantDefault, setLanguage, clearLanguage])

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
}

/**
 * The current language and the controls that change it.
 *
 * Safe outside the provider: it answers with the resolved chain rather than throwing, so a component
 * lifted into a test harness or a public page renders text instead of a crash. A language is never
 * worth a blank screen.
 */
export function useLanguage() {
  const ctx = useContext(LanguageContext)
  if (ctx) return ctx
  return {
    lang: resolveLanguage({ offered: LANGUAGES }),
    offered: LANGUAGES,
    baseLanguage: BASE_LANGUAGE,
    accountLanguage: null,
    tenantDefault: null,
    setLanguage: () => {},
    clearLanguage: () => {},
  }
}

/**
 * The getter for one namespace, in the current language.
 *
 *   const t = useT('auth')
 *   <h1>{t('welcomeBack')}</h1>
 *
 * A missing string renders ENGLISH. Never a key, never a blank.
 */
export function useT(ns = 'common') {
  const { lang } = useLanguage()
  return useMemo(() => makeT(namespaceDict(ns), lang), [ns, lang])
}
